import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  type SxProps,
  type Theme
} from '@mui/material';

interface CounterProps {
  title: string;
  count: number;
  sx?: SxProps<Theme>;
}

const Counter: React.FC<CounterProps> = ({ title, count, sx }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Mobile layout - stacked
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, ...sx }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '24px',
            color: 'text.primary',
            letterSpacing: '0.15px'
          }}
        >
          {title}
        </Typography>
        {count > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                flexShrink: 0
              }}
            />
            <Typography
              component="span"
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'text.secondary',
                lineHeight: '20px'
              }}
            >
              {count} {count === 1 ? 'item' : 'items'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Desktop layout - inline
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 1,
        minWidth: 0,
        ...sx
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontFamily: 'Roboto',
          fontSize: '34px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '42px',
          letterSpacing: '0.25px',
          color: theme.palette.primary.main,
          minWidth: 0,
          wordBreak: 'break-word'
        }}
      >
        {title}
      </Typography>
      {count > 0 && (
        <Typography
          component="span"
          sx={{
            color: theme.palette.primary.main,
            fontSize: '34px',
            fontWeight: 600,
            lineHeight: '42px',
            letterSpacing: '0.25px',
            flexShrink: 0
          }}
        >
          · {count}
        </Typography>
      )}
    </Box>
  );
};

export default Counter;
